/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  EMAIL_PATTERN_VALIDATION,
  Nullable,
  URL_PATTERN_VALIDATION,
  isEmptyOrNull
} from '@SLR/shared-library';
import {
  ChangeOrganizationDraftRequestDto,
  CreateOrganizationDraftRequestDto,
  OrganizationType
} from '@SLR/user-service-full-sdk';
import { OnboardingType } from 'configs';
import { ObjectSchema, array, boolean, mixed, object, string } from 'yup';

export const URL_MAX_CHAR_LENGTH = 255;
export const NAME_MAX_CHAR_LENGTH = 200;
export const DESCRIPTION_MAX_CHAR_LENGTH = 400;
export const INFORMATION_MAX_CHAR_LENGTH = 255;
export const PERSON_MAX_CHAR_LENGTH = 255;
export const SOLUTION_INFORMATION_MAX_CHAR_LENGTH = 500;
export const COMMENT_CHAR_LENGTH = 1500;

export const MemberEmailSchema: ObjectSchema<{ email: string }> =
  object().shape({
    email: string()
      .test(
        'valid',
        'draftPage.formError.invalidEmail',
        (value) => isEmptyOrNull(value) || EMAIL_PATTERN_VALIDATION.test(value)
      )
      .default(null)
  });

export type OrganizationDraftInput = Nullable<
  CreateOrganizationDraftRequestDto & ChangeOrganizationDraftRequestDto
> & {
  emails: { email: string }[] | null;
  newComment?: string | null;
};

export const OrganizationDraftSchema = (
  onboardingType: OnboardingType
): ObjectSchema<OrganizationDraftInput> =>
  object().shape({
    additionalInformation: string()
      .max(INFORMATION_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .default(null)
      .nullable(),
    comment: string()
      .max(COMMENT_CHAR_LENGTH, 'form.maxCharLengthError')
      .default(null)
      .nullable(),
    newComment: string()
      .max(COMMENT_CHAR_LENGTH, 'form.maxCharLengthError')
      .default(null)
      .nullable(),
    contactEmailAddress: string()
      .required('draftPage.formError.contactEmailAddress')
      .matches(EMAIL_PATTERN_VALIDATION, 'draftPage.formError.invalidEmail')
      .test(
        'isEmpty',
        'draftPage.formError.contactEmailAddress',
        (value) => !isEmptyOrNull(value)
      )
      .default(null),
    contactPerson: string()
      .required('draftPage.formError.contactPerson')
      .max(NAME_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .test(
        'isEmpty',
        'draftPage.formError.contactPerson',
        (value) => !isEmptyOrNull(value)
      )
      .default(null),
    geoAreaId: string().default(null).nullable(),
    geoAreaIdOrAdditionalInformationSpecified: boolean().default(false),
    emails: array().of(MemberEmailSchema.defined()).nullable().default(null),
    memberEmailAddresses: array()
      .of(string().default(null))
      .nullable()
      .default([]),
    name: string()
      .required(`draftPage.formError.name.${onboardingType}`)
      .max(NAME_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .test(
        'isEmpty',
        `draftPage.formError.name.${onboardingType}`,
        (value) => !isEmptyOrNull(value)
      )
      .default(null),
    description: string()
      .required('draftPage.formError.description')
      .max(DESCRIPTION_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .test(
        'isEmpty',
        'draftPage.formError.description',
        (value) => !isEmptyOrNull(value)
      )
      .default(null),
    notUserOrganization: boolean().default(true),
    solutionInformation: string()
      .test(
        'required',
        'draftPage.formError.solutionInformation',
        (value) =>
          !isEmptyOrNull(value) ||
          onboardingType !== OnboardingType.SOLUTION_PROVIDER
      )
      .max(SOLUTION_INFORMATION_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .nullable()
      .default(null),
    solutionInformationSpecified: boolean().default(false),
    type: mixed<OrganizationType>().nullable().default(null),
    website: string()
      .max(URL_MAX_CHAR_LENGTH, 'form.maxCharLengthError')
      .nullable()
      .test(
        'matches',
        'draftPage.formError.invalidWebsite',
        (value) => isEmptyOrNull(value) || URL_PATTERN_VALIDATION.test(value)
      )
      .default(null)
  });
